import { PricePromoUnit, PricePromoWatchlist, PricePromoWatchlistRegion } from '../../services/api';

/** Budget tolerance: a unit matches when its price is within 10% either side of the watchlist budget. */
export const WATCHLIST_BUDGET_TOLERANCE = 0.1;

/** Sentinel bedroom value for penthouses (matches Super Picker's tower view). */
export const PENTHOUSE_FILTER_VALUE = -1;

/** Same penthouse rule as Super Picker's tower view: unit type mentions PH/penthouse. */
const isPenthouseText = (value: unknown): boolean => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return false;
  return raw.includes('penthouse') || raw.includes('penthhouse') || /\bph\b/.test(raw) || raw.endsWith('ph');
};

export const isPenthouseUnit = (unit: PricePromoUnit): boolean =>
  isPenthouseText(unit.unitTypeDescription) || isPenthouseText(unit.unitType);

/** Window (days) for a watchlist's "Latest Price Promo" section, matching the main dialog. */
export const WATCHLIST_LATEST_DAYS = 14;

/** Human-readable summary of the matching rules, shown in the watchlist UI. */
export const WATCHLIST_MATCH_EXPLANATION =
  `Matches are price promo units with one of the selected bedroom counts, a price within ±${WATCHLIST_BUDGET_TOLERANCE * 100}% ` +
  'of the budget and, when set, a selected region and district — counting promos that came in after the watchlist was created. ' +
  'A newly created watchlist shows the single latest promo that fits the criteria.';

/** Extra note for the watchlist detail view, where the all-time "All Price Promo" section is shown. */
export const WATCHLIST_ALL_SECTION_EXPLANATION =
  'All Price Promo lists every unit that fits the criteria, including promos from before the watchlist was created.';

/** Synthetic id for the built-in Demo Client watchlist — never collides with DB ids. */
export const DEMO_WATCHLIST_ID = -1;

/** Shown alongside the matching rules when the built-in demo watchlist is present. */
export const DEMO_WATCHLIST_EXPLANATION =
  'The Demo Client watchlist is a built-in example that always tracks the latest price promo — ' +
  'it cannot be edited and does not count towards your quota.';

/** Budget rounding steps for the demo watchlist, coarsest first. */
const DEMO_BUDGET_STEPS = [500_000, 100_000, 50_000, 10_000, 1_000];

export function normalizeWatchlistRegion(value?: string | null): PricePromoWatchlistRegion | null {
  if (!value) return null;
  const upper = String(value).toUpperCase();
  if (upper.includes('CCR')) return 'CCR';
  if (upper.includes('RCR')) return 'RCR';
  if (upper.includes('OCR')) return 'OCR';
  return null;
}

/** "D05" / "d5" / "5" → "D5"; anything outside D1–D28 → null. */
export function normalizeWatchlistDistrict(value?: string | null): string | null {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(String(value).trim().replace(/^D/i, ''));
  if (!Number.isInteger(num) || num < 1 || num > 28) return null;
  return `D${num}`;
}

function changeAtMs(unit: PricePromoUnit): number {
  if (!unit.priceChangeAt) return 0;
  const t = new Date(unit.priceChangeAt).getTime();
  return Number.isFinite(t) ? t : 0;
}

function parseIsoMs(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Built-in "Demo Client" watchlist derived from the latest price promo unit:
 * same bedroom count, no region filter, and the budget rounded up to the
 * coarsest step that stays within 10% of that unit's price, so the demo
 * budget reads realistically (e.g. a $4,329,000 promo yields a $4,500,000
 * budget). Frontend-only — pinned above
 * the agent's watchlists and excluded from the quota. Returns null when the
 * promo feed is empty. The epoch createdAt makes every criteria match count
 * as "since creation", so opening the demo always shows the latest promos.
 */
export function buildDemoWatchlist(units: PricePromoUnit[]): PricePromoWatchlist | null {
  const latest = units
    .filter((u) => u.price > 0 && changeAtMs(u) > 0)
    .sort((a, b) => changeAtMs(b) - changeAtMs(a))[0];
  if (!latest) return null;

  let budget = Math.round(latest.price);
  for (const step of DEMO_BUDGET_STEPS) {
    const rounded = Math.ceil(latest.price / step) * step;
    if (latest.price >= rounded * (1 - WATCHLIST_BUDGET_TOLERANCE)) {
      budget = rounded;
      break;
    }
  }

  return {
    id: DEMO_WATCHLIST_ID,
    clientName: 'Demo Client',
    mobile: '90000001',
    bedrooms: [isPenthouseUnit(latest) ? PENTHOUSE_FILTER_VALUE : latest.bedroomNumber],
    budget,
    regions: [],
    districts: [],
    lastViewedAt: null,
    createdAt: null,
    updatedAt: null,
  };
}

/** Criteria-only check: bedrooms, budget ±10% range and (when set) regions / districts. */
export function unitMatchesWatchlistCriteria(unit: PricePromoUnit, watchlist: PricePromoWatchlist): boolean {
  // PENTHOUSE_FILTER_VALUE matches penthouses only; numeric counts match non-penthouses,
  // mirroring the Price Promo dialog's bedroom chips.
  const unitIsPenthouse = isPenthouseUnit(unit);
  const matchesBedroom = watchlist.bedrooms.some((b) =>
    b === PENTHOUSE_FILTER_VALUE ? unitIsPenthouse : !unitIsPenthouse && b === unit.bedroomNumber
  );
  if (!matchesBedroom) return false;
  const budget = watchlist.budget;
  if (!(budget > 0) || !(unit.price > 0)) return false;
  if (unit.price < budget * (1 - WATCHLIST_BUDGET_TOLERANCE)) return false;
  if (unit.price > budget * (1 + WATCHLIST_BUDGET_TOLERANCE)) return false;
  if (watchlist.regions.length > 0) {
    const unitRegion = normalizeWatchlistRegion(unit.region);
    if (!unitRegion || !watchlist.regions.includes(unitRegion)) return false;
  }
  if (watchlist.districts.length > 0) {
    const unitDistrict = normalizeWatchlistDistrict(unit.district);
    if (!unitDistrict) return false;
    const wanted = watchlist.districts.map((d) => normalizeWatchlistDistrict(d));
    if (!wanted.includes(unitDistrict)) return false;
  }
  return true;
}

export interface WatchlistMatchResult {
  /**
   * Criteria matches whose price change came in after the watchlist was
   * created, newest first. When there are none (e.g. a newly created
   * watchlist), falls back to the single latest all-time criteria match and
   * `usedFallback` is true.
   */
  matches: PricePromoUnit[];
  usedFallback: boolean;
  /** `matches` within the last WATCHLIST_LATEST_DAYS days — "Latest Price Promo" section. */
  latest: PricePromoUnit[];
  /** Top 5 of `matches` by saving (most negative priceChange first) — "Top 5 Saving" section. */
  topSaving: PricePromoUnit[];
  /**
   * Every criteria match regardless of when the promo came in, newest first —
   * "All Price Promo" section. A superset of `matches`.
   */
  all: PricePromoUnit[];
  /** Matches newer than the agent's last view of this watchlist (drives blink / Linus popup). */
  newMatchCount: number;
}

export function getWatchlistMatches(
  units: PricePromoUnit[],
  watchlist: PricePromoWatchlist,
  now: number = Date.now(),
): WatchlistMatchResult {
  const criteriaMatches = units
    .filter((u) => unitMatchesWatchlistCriteria(u, watchlist))
    .sort((a, b) => changeAtMs(b) - changeAtMs(a));

  const createdAtMs = parseIsoMs(watchlist.createdAt);
  const sinceCreation = criteriaMatches.filter((u) => changeAtMs(u) > createdAtMs);

  const usedFallback = sinceCreation.length === 0 && criteriaMatches.length > 0;
  const matches = usedFallback ? criteriaMatches.slice(0, 1) : sinceCreation;

  const latestCutoff = now - WATCHLIST_LATEST_DAYS * 24 * 60 * 60 * 1000;
  const latest = usedFallback ? matches : matches.filter((u) => changeAtMs(u) >= latestCutoff);

  const topSaving = [...matches].sort((a, b) => a.priceChange - b.priceChange).slice(0, 5);

  const seenSinceMs = Math.max(parseIsoMs(watchlist.lastViewedAt), createdAtMs);
  const newMatchCount = sinceCreation.filter((u) => changeAtMs(u) > seenSinceMs).length;

  return { matches, usedFallback, latest, topSaving, all: criteriaMatches, newMatchCount };
}

/** Watchlists that currently have unseen matches, for the sidebar blink + Linus popup. */
export function getWatchlistsWithNewMatches(
  units: PricePromoUnit[],
  watchlists: PricePromoWatchlist[],
  now: number = Date.now(),
): PricePromoWatchlist[] {
  return watchlists.filter((w) => getWatchlistMatches(units, w, now).newMatchCount > 0);
}
